import {inject, Injectable} from '@angular/core';
import {environment} from '@environments/environment';
import {Brand, DefaultPartsSearchBrand} from '@service-and-repairs/awpintegrationlib';
import {CustomerWithVehicles} from '@service-and-repairs/calm-leads-customer-lib-api';
import {
  AppointmentInfo,
  AppointmentSearchInput,
  AppointmentSearchResult,
  Brand as DmsBrand,
  Customer,
  CustomerAddressEnum,
  CustomerDataResult,
  CustomerVehicleRelationEnum,
  DcomDateFormat,
  DealerData,
  Dms,
  EtkLicensePlateSearchInput,
  EtkSettings,
  EtkVehicleInfo,
  ExtendedVehicleInfo,
  OrderInfo,
  OrderSearchInput,
  OrderSearchResult,
  PricesAndAvailabilitiesRequestMethod,
  Settings,
  TransferFormat,
  TransferMode,
  Unsubscribe,
  VehicleSearchInput,
  VehicleSearchResult
} from '@service-and-repairs/dms-api';
import {IntercomServer} from '@service-and-repairs/dms-common';
import {DialogCoordinatorImpl, DmsImpl, DmsStub} from '@service-and-repairs/dms-impl';
import {Observable, ReplaySubject, Subject} from 'rxjs';
import {EtkConfiguration} from '../../core/configuration/interfaces/etk-configuration';
import {OutletConfiguration} from '../../core/configuration/interfaces/outlet-configuration';
import {OutletRelatedConfiguration} from '../../core/configuration/interfaces/outlet-related-configuration';
import {ConfigurationLoader} from '../../core/configuration/services/configuration.loader';
import {UserData} from '../../core/user/models/user-data';
import {UserService} from '../../core/user/services/user.service';
import {ThinClientModeService} from '../../services/thin-client-mode.service';
import {Util} from '../../util/util';
import {AwpDmsInterfaceGateway} from './awp-dms-interface/adapter/awp-dms-interface-gateway.service';
import {GetVehicleResponse, GetVehicleResponseBody, VehicleCustomerReference} from './awp-dms-interface/models/vehicle';
import {CustomerSelectionService} from './customer-selection/customer-selection.service';
import {DcomDmsSettings} from './dcom-dms-settings';
import {DmsCustomerDetermination} from './dms-customer-determination';
import {DmsSettings} from './dms-settings';
import {DmsSystem} from './dms-system';
import {OrderAndAppointmentSearchResult} from './order-and-appointment-search-result';

@Injectable({
  providedIn: 'root'
})
export class DmsService {

  // region Members and constructor

  private readonly configurationLoader: ConfigurationLoader = inject(ConfigurationLoader);
  private readonly userService: UserService = inject(UserService);
  private readonly awpDmsGateway: AwpDmsInterfaceGateway = inject(AwpDmsInterfaceGateway);
  private readonly customerSelectionService: CustomerSelectionService = inject(CustomerSelectionService);

  // DMS Transfer
  dmsSettings: DmsSettings = new DmsSettings();
  private outletRelatedConfig: OutletRelatedConfiguration;
  private etkConfig: EtkConfiguration;

  /**
   * Will be triggered whenever any general or ETK setting changes
   */
  dmsSettingsChanged: Observable<Settings>;
  private readonly dmsSettingsChangedSubject: ReplaySubject<Settings> = new ReplaySubject<Settings>(1);
  private previousOutletConfig: OutletRelatedConfiguration;
  private previousEtkConfig: EtkConfiguration;
  private previousUserDmsSettings: string;

  /**
   * Emits whenever a single DMS customer ID was determined for a VIN via the DMS lib, e.g. because an app (or the
   * webapp itself) let the user select a DMS customer.
   */
  dmsCustomerDetermined: Observable<DmsCustomerDetermination>;
  private readonly dmsCustomerDeterminedSubject: Subject<DmsCustomerDetermination> =
    new Subject<DmsCustomerDetermination>();
  private unsubscribeFromDmsCustomerDetermination: Unsubscribe;

  dmsApi: Dms;
  private dmsIntercom: IntercomServer = null;

  constructor() {
    const userService: UserService = this.userService;

    this.dmsSettingsChanged = this.dmsSettingsChangedSubject.asObservable();
    this.dmsCustomerDetermined = this.dmsCustomerDeterminedSubject.asObservable();
    userService.user$.subscribe((): void => this.handleUserChanges());
  }

  // endregion

  // region  Static methods to simplify unit tests

  private static loadDmsScripts(): void {
    Util.attachScript('/api/gui/dms/static/dms-ui/main.mjs').catch((): void => {
      console.error('DmsService: Failed to load dms-ui/main.mjs');
    });
    Util.attachScript('/api/gui/dms/static/dms-ui/polyfills.js').catch((): void => {
      console.error('DmsService: Failed to load dms-ui/polyfills.js');
    });
  }

  private static getIntercomServer(): IntercomServer {
    return new IntercomServer('dms');
  }

  private static createDmsStub(dmsApi: Dms, dmsIntercom: IntercomServer): void {
    // Creating a new instance is sufficient, no need to keep a reference
    new DmsStub(dmsApi, dmsIntercom); // NOSONAR
  }

  private static createDmsImpl(dmsApiTransferSettings: Settings, dealerData: DealerData): DmsImpl {
    return new DmsImpl(dmsApiTransferSettings, dealerData, new DialogCoordinatorImpl());
  }

  private static mapDefaultPartsSearchBrand(brand: DefaultPartsSearchBrand): DmsBrand | undefined {
    switch (brand) {
      case DefaultPartsSearchBrand.BMW:
        return DmsBrand.Bmw;
      case DefaultPartsSearchBrand.BMW_I:
        return DmsBrand.Bmwi;
      case DefaultPartsSearchBrand.MINI:
        return DmsBrand.Mini;
      case DefaultPartsSearchBrand.BMW_MOTORRAD:
        return DmsBrand.BmwMotorcycle;
      case DefaultPartsSearchBrand.ROLLS_ROYCE:
        return DmsBrand.RollyRoyce;
      default:
        return undefined;
    }
  }

  // endregion

  // region Public DMS vehicle methods

  searchVehiclesByLicensePlateViaAwpDmsInterface(licensePlate: string,
                                                 resultSize: number): Promise<VehicleSearchResult> {
    const user: UserData = this.getUser();
    return this.awpDmsGateway.searchVehiclesByLicensePlate(
      licensePlate,
      resultSize,
      user.getLocale(),
      this.getDpNo(user),
      this.getOutletNo(user)
    );
  }

  searchVehiclesByLicensePlateViaDcom(licensePlate: string, resultSize: number): Promise<VehicleSearchResult> {
    if (this.dmsApi) {
      const input: VehicleSearchInput = new VehicleSearchInput(licensePlate, resultSize);
      return this.dmsApi.searchVehicles(input);
    } else {
      return Promise.reject(new Error('Dms is not initialized'));
    }
  }

  searchVehiclesByLicensePlateViaEtkDms(licensePlate: string): Promise<EtkVehicleInfo> {
    if (this.dmsApi) {
      const input: EtkLicensePlateSearchInput = new EtkLicensePlateSearchInput(licensePlate, undefined);
      return this.dmsApi.searchVehiclesByLicensePlateViaEtkDms(input);
    } else {
      return Promise.reject(new Error('Dms is not initialized'));
    }
  }

  getLicensePlateByVin(vin17: string): Promise<string> {
    if (this.isAwpDmsInterfaceEnabled()) {
      return this.getLicensePlateByVinViaAwpDmsInterface(vin17);
    } else {
      return this.getLicensePlateByVinViaDcom(vin17);
    }
  }

  // endregion

  // region Public DMS order and appointment methods

  searchOrdersViaAwpDmsInterface(orderNumber: string | undefined, vin: string | undefined,
                                 resultSize: number): Promise<OrderSearchResult> {
    if (this.isProdUsEnvironment()) {
      console.info(
        '[Webapp] [DmsService] searchOrdersViaAwpDmsInterface is not yet enabled in PROD_US. Returning empty result.');
      return Promise.resolve(new OrderSearchResult([], 0));
    }
    const user: UserData = this.getUser();
    return this.awpDmsGateway.searchOrders(
      orderNumber,
      vin,
      resultSize,
      user.getLocale(),
      this.getDpNo(user),
      this.getOutletNo(user)
    );
  }

  searchOrderViaDcom(orderNumber: string, vin: string, resultSize: number): Promise<OrderSearchResult> {
    if (this.dmsApi) {
      const input: OrderSearchInput = new OrderSearchInput(
        orderNumber,
        null,
        null,
        null,
        vin?.length === 7 ? '*' + vin : vin,
        null,
        resultSize
      );
      return this.dmsApi.searchOrders(input);
    } else {
      return Promise.reject(new Error('Dms is not initialized'));
    }
  }

  searchAppointmentViaDcom(serviceAppointmentId: string,
                           vin: string,
                           resultSize: number): Promise<AppointmentSearchResult> {
    if (this.dmsApi) {
      const input: AppointmentSearchInput = new AppointmentSearchInput(
        serviceAppointmentId,
        null, null, null, null, null,
        vin?.length === 7 ? '*' + vin : vin,
        null, null, null,
        resultSize
      );
      return this.dmsApi.searchAppointments(input);
    } else {
      return Promise.reject(new Error('Dms is not initialized'));
    }
  }

  async searchOrdersAndAppointmentsViaDmsInterface(vin: string): Promise<OrderAndAppointmentSearchResult[]> {
    // AWP-DMS-Interface enabled
    if (this.isAwpDmsInterfaceEnabled()) {
      try {
        const orderResult: OrderSearchResult = await this.searchOrdersViaAwpDmsInterface(undefined, vin, 100);
        return this.mergeOrderAndAppointmentResults(orderResult.orders ?? [], []);
      } catch (error) {
        // searchServiceOrders may not be supported by all DMS middleware implementations (e.g., US market).
        // If it fails, we gracefully return an empty result so vehicle lookup and service case opening can continue.
        console.warn(
          '[Webapp] [DmsService] Failed to search orders via AWP-DMS-Interface. Continuing without order results.',
          error
        );
        return [];
      }
    }

    // No interface enabled
    if (this.dmsSettings.transferMode !== TransferMode.Dcom) {
      return Promise.resolve([]);
    }

    // DCOM-DMS-Interface enabled
    const [orderResult, appointmentResult] = await Promise.allSettled([
      this.searchOrderViaDcom(undefined, vin, 100),
      this.searchAppointmentViaDcom(undefined, vin, 100)
    ]);
    const orderResults: OrderInfo[] = orderResult.status === 'fulfilled' ? orderResult.value.orders : [];
    const appointmentResults: AppointmentInfo[] = appointmentResult.status === 'fulfilled' ?
      appointmentResult.value.appointments :
      [];
    return this.mergeOrderAndAppointmentResults(orderResults, appointmentResults);
  }

  searchOrderViaEtkDms(orderNumber: string): Promise<EtkVehicleInfo> {
    if (this.dmsApi) {
      return this.dmsApi.searchVehiclesByOrderNumerViaEtkDms(orderNumber);
    } else {
      return Promise.reject(new Error('Dms is not initialized'));
    }
  }

  // endregion

  // region Public DMS customer methods

  async getCustomerLanguageAndNumber(vin17: string): Promise<string[]> {
    if (this.isAwpDmsInterfaceEnabled()) {
      return this.getCustomerLanguageAndNumberViaAwpDmsInterface(vin17);
    } else {
      return this.getCustomerLanguageAndNumberViaDmsLib(vin17);
    }
  }

  async searchCustomersByNameViaAwpDmsInterface(firstName: string, lastName: string): Promise<CustomerWithVehicles[]> {
    const user: UserData = this.getUser();
    try {
      return await this.awpDmsGateway.searchCustomersByNameViaAwpDmsInterface(
        firstName,
        lastName,
        user.getLocale(),
        this.getDpNo(user),
        this.getOutletNo(user)
      );
    } catch (error) {
      // searchCustomers may not be supported by all DMS middleware implementations (e.g., US market).
      // If it fails, we gracefully return an empty result so the flow can continue.
      console.warn(
        '[Webapp] [DmsService] Failed to search customers by name via AWP-DMS-Interface. Continuing without customer results.',
        error
      );
      return [];
    }
  }

  searchCustomersByVinViaAwpDmsInterface(vin17: string): Promise<CustomerWithVehicles[]> {
    if (this.isUsEnvironment()) {
      console.info(
        '[Webapp] [DmsService] searchCustomersByVinViaAwpDmsInterface is not supported in US environment. Returning empty result.');
      return Promise.resolve([]);
    } else {
      const user = this.getUser();
      return this.awpDmsGateway.searchCustomersByVin(
        vin17,
        user.getLocale(),
        this.getDpNo(user),
        this.getOutletNo(user)
      );
    }
  }

  // endregion

  // region Private DMS access methods

  private async getCustomerLanguageAndNumberViaAwpDmsInterface(vin17: string): Promise<string[]> {
    const user: UserData = this.getUser();
    const locale: string = user.getLocale();
    let getVehicleResponseBody: GetVehicleResponseBody;
    try {
      const response: GetVehicleResponse = await this.awpDmsGateway.awpService.getVehicle(
        vin17,
        locale,
        this.getDpNo(user),
        this.getOutletNo(user)
      );
      getVehicleResponseBody = response.body;
    } catch (error) {
      console.error(
        'AWP-DMS-Interface (DmsService): Failed to get vehicle data, customerDmsId remains unknown.',
        error
      );
      return [];
    }

    const customers: VehicleCustomerReference[] = getVehicleResponseBody?.customerReferenceList ?? [];

    let customer: VehicleCustomerReference;
    if (customers.length === 0) {
      console.warn('AWP-DMS-Interface (DmsService): getVehicle() found no customers associated with the vehicle.');
      customer = undefined;
    } else if (customers.length === 1) {
      console.info('AWP-DMS-Interface (DmsService): getVehicle() found 1 customer associated with the vehicle.');
      customer = customers[0];
    } else {
      console.info('AWP-DMS-Interface (DmsService): getVehicle() found '
        + customers.length
        + ' customers associated with the vehicle.');
      try {
        customer = await this.customerSelectionService.selectCustomer(customers);
      } catch {
        // User canceled selection - fall back to default selection logic
        console.warn('AWP-DMS-Interface (DmsService): Customer selection cancelled, using default selection.');
        customer = customers.find((customerReference: VehicleCustomerReference): boolean =>
          customerReference.relationType === 'USER') ?? customers[0];
      }
    }

    if (!customer) {
      return [];
    }

    // Customer language is not available from the AWP-DMS-Interface's getVehicle() call, default to user's locale.
    return [locale, customer.dmsCustomerId];
  }

  /**
   * Retrieves the preferred language and DMS customer number for the primary customer associated with a vehicle.
   *
   * The method first identifies the primary customer (USER relation) for the given VIN, then attempts to determine
   * their language preference. If no explicit language is set, it falls back to inferring the language from the
   * customer's main address country.
   *
   * @param vin17 The 17-character Vehicle Identification Number
   * @returns A promise resolving to a tuple [language, dmsCustomerNumber], or rejects if no customer is found
   */
  private getCustomerLanguageAndNumberViaDmsLib(vin17: string): Promise<string[]> {
    return new Promise<string[]>((resolve, reject): void => {
      Promise.all([
            this.dmsApi.determineSingleCustomerId(vin17, undefined, [CustomerVehicleRelationEnum.USER]),
            this.dmsApi.getCustomerDataByVin(vin17)
          ]
        )
        .then((results: [string, CustomerDataResult]): void => {
          const chosenCustomer: Customer = results[1].customerList
            .find((customer: Customer): boolean => customer.customerId.dmsId === results[0]);
          if (chosenCustomer) {

            // look for language
            const language: string = chosenCustomer.personalData?.language?.internalKey;
            if (language) {
              console.log('DmsService: Got language ' + language + ' from DMS.');
              resolve([this.normalizeLanguage(language), chosenCustomer.customerId?.dmsId]);
              return;
            }

            // determine from address if language cannot be found
            let addressCountry: string;
            switch (chosenCustomer.contactData.mainAddress) {
              case CustomerAddressEnum.AdditionalAddress:
                addressCountry = chosenCustomer.contactData.additionalAddress?.country.internalKey;
                break;
              case CustomerAddressEnum.BusinessAddress:
                addressCountry = chosenCustomer.contactData.businessAddress?.country.internalKey;
                break;
              case CustomerAddressEnum.PrivateAddress:
                addressCountry = chosenCustomer.contactData.privateAddress?.country.internalKey;
                break;
            }
            console.log('DmsService: Got country ' + addressCountry + ' from DMS.');
            resolve([this.normalizeLanguage(addressCountry), chosenCustomer.customerId?.dmsId]);
          } else {
            // no chosen customer found
            resolve([]);
          }
        })
        // DMS requests failed
        .catch(reject);
    });
  }

  private async getLicensePlateByVinViaAwpDmsInterface(vin17: string): Promise<string> {
    const user: UserData = this.getUser();
    try {
      const response: GetVehicleResponse = await this.awpDmsGateway.awpService.getVehicle(
        vin17,
        user.getLocale(),
        this.getDpNo(user),
        this.getOutletNo(user)
      );
      return response?.body?.vehicle?.numberPlate ?? '';
    } catch (error) {
      console.error('DmsService: Failed to get license plate by VIN via AWP-DMS-Interface', error);
      return '';
    }
  }

  private getLicensePlateByVinViaDcom(vin17: string): Promise<string> {
    // TODO: is triggered also in browser, if file or clipboard configured
    if (this.dmsApi) {
      return new Promise<string>((resolve, reject): void => {
        this.dmsApi.getVehicleDataByVin(vin17)
          .then((vehicle: ExtendedVehicleInfo) => resolve(vehicle.numberPlate))
          // DMS requests failed
          .catch(reject);
      });
    } else {
      return Promise.resolve(null);
    }
  }

  // endregion

  // region Public helper/converter methods

  /**
   * Returns true if DMS orders have to be in the customer's language.
   * Currently, this is the case in Belgium and Luxembourg in ThinClient mode.
   */
  dmsOrderHasToBeInCustomerLanguage(): boolean {
    const userCountry: string = this.getUser().getB2XCountryCode();
    if ((userCountry === 'BE' || userCountry === 'LU') && ThinClientModeService.thinClientMode) {
      return this.outletRelatedConfig?.outletConfiguration?.multipleLanguageCheckAllowed;
    } else {
      return false;
    }
  }

  getWorkplaceId(brand: string): string {
    let id: string;
    if (this.dmsSettings.dcom.workplaceId) {
      id = this.dmsSettings.dcom.workplaceId;
    } else {
      switch (brand) {
        case Brand.BMW:
          id = this.dmsSettings.dcom.bmwWorkplaceId;
          break;
        case Brand.BMW_i:
          id = this.dmsSettings.dcom.bmwiWorkplaceId;
          break;
        case Brand.MINI:
          id = this.dmsSettings.dcom.miniWorkplaceId;
          break;
        case Brand.RR:
          id = this.dmsSettings.dcom.rollsRoyceWorkplaceId;
          break;
        case Brand.ZINORO:
          id = this.dmsSettings.dcom.zinoroWorkplaceId;
          break;
        case Brand.MOT:
          id = this.dmsSettings.dcom.bmwMotorradWorkplaceId;
          break;
      }
    }
    return id;
  }

  // endregion

  // region is-something-enabled methods

  isAwpDmsInterfaceEnabled(): boolean {
    return !!this.dmsSettings.awp.isAwpDmsInterfaceEnabled;
  }

  isCustomerSearchEnabled(source: DmsSystem): boolean {
    switch (source) {
      case DmsSystem.ANY:
        return (this.isAwpDmsInterfaceEnabled() && !this.isUsEnvironment()) || this.isDcomCustomerSearchEnabled();
      case DmsSystem.AWP_DMS_INTERFACE:
        return this.isAwpDmsInterfaceEnabled() && !this.isUsEnvironment(); // Not supported by US RO Hub as of Feb 2026
      case DmsSystem.DCOM:
        return this.isDcomCustomerSearchEnabled();
      case DmsSystem.ETK:
        return false; // not supported
    }
  }

  isOrderSearchEnabled(source: DmsSystem): boolean {
    switch (source) {
      case DmsSystem.ANY:
        return (this.isAwpDmsInterfaceEnabled() && !this.isProdUsEnvironment())
          || this.isDcomOrderSearchEnabled()
          || this.isEtkOrderSearchEnabled();
      case DmsSystem.AWP_DMS_INTERFACE:
        return this.isAwpDmsInterfaceEnabled() && !this.isProdUsEnvironment();
      case DmsSystem.DCOM:
        return this.isDcomOrderSearchEnabled();
      case DmsSystem.ETK:
        return this.isEtkOrderSearchEnabled();
    }
  }

  isLicensePlateSearchEnabled(source: DmsSystem): boolean {
    switch (source) {
      case DmsSystem.ANY:
        return (this.isAwpDmsInterfaceEnabled() && !this.isUsEnvironment())
          || this.isDcomLicensePlateSearchEnabled()
          || this.isEtkLicensePlateSearchEnabled();
      case DmsSystem.AWP_DMS_INTERFACE:
        return this.isAwpDmsInterfaceEnabled() && !this.isUsEnvironment(); // Not supported by US RO Hub as of Feb 2026
      case DmsSystem.DCOM:
        return this.isDcomLicensePlateSearchEnabled();
      case DmsSystem.ETK:
        return this.isEtkLicensePlateSearchEnabled();
    }
  }

  private isDcomLicensePlateSearchEnabled(): boolean {
    return ThinClientModeService.thinClientMode
      && this.dmsSettings.dcom.url?.trim()?.length > 0
      && this.dmsSettings.dcom.isLicensePlateSearchEnabled;
  }

  private isEtkLicensePlateSearchEnabled(): boolean {
    return ThinClientModeService.thinClientMode && this.dmsSettings.etk.isEnabled;
  }

  private isDcomOrderSearchEnabled(): boolean {
    return ThinClientModeService.thinClientMode && this.dmsSettings.dcom.url?.trim()?.length > 0;
  }

  private isEtkOrderSearchEnabled(): boolean {
    return ThinClientModeService.thinClientMode && this.dmsSettings.etk.isEnabled;
  }

  private isDcomCustomerSearchEnabled(): boolean {
    return ThinClientModeService.thinClientMode && this.dmsSettings.dcom.url?.trim()?.length > 0;
  }

  private isWritingToDmsEnabled(): boolean {
    return this.isAwpDmsInterfaceEnabled() || this.outletRelatedConfig?.outletConfiguration?.dmsWriteAccessAllowed;
  }

  private isWritingToClipboardEnabled(): boolean {
    return this.outletRelatedConfig?.outletConfiguration?.dmsWriteAccessAllowed
      && this.outletRelatedConfig?.outletConfiguration?.transferMode === TransferMode.Clipboard;
  }

  private isWritingToUsDirectIsEnabled(): boolean {
    return this.etkConfig?.dmsDirect && this.etkConfig?.dmsDirectPath !== undefined;
  }

  // endregion

  // region Other private methods

  private init(): void {
    this.dmsIntercom = DmsService.getIntercomServer();
    this.dmsIntercom.start();
    this.subscribeToConfigChanges();
  }

  private handleUserChanges(): void {
    const user = this.getUser();
    if (user) {
      if (this.dmsIntercom) {
        this.instantiateDmsApi();
      } else {
        this.init();
      }
    }
  }

  private subscribeToConfigChanges(): void {
    this.configurationLoader.configChanged.subscribe((config: any): void => {
      this.outletRelatedConfig = config.outletRelatedConfig;
      this.etkConfig = config.etkConfig;
      this.initializeDms();
    });
  }

  private initializeDms(): void {
    this.calculateDmsSettingsFromConfig();
    this.instantiateDmsApi();
  }

  private calculateDmsSettingsFromConfig(): void {
    this.dmsSettings.awp.isAwpDmsInterfaceEnabled =
      this.outletRelatedConfig?.outletConfiguration?.awpDmsInterfaceEnabled;
    this.dmsSettings.serviceContractTransferMethod =
      this.outletRelatedConfig?.outletConfiguration?.serviceContractDmsTransferMethod;
    this.dmsSettings.serviceContractTransferNumber =
      this.outletRelatedConfig?.outletConfiguration?.serviceContractDmsTransferNumber;
    if (ThinClientModeService.thinClientMode) {
      this.calculateSettingsForThinClientMode();
    } else {
      this.calculateSettingsForBrowserMode();
    }
  }

  private isDcomConfigured(): boolean {
    return this.outletRelatedConfig?.outletConfiguration?.dcomUrl?.trim().length > 0;
  }

  private isEtkDmsConfigured(): boolean {
    return !!this.etkConfig?.dmsAnbindung && this.etkConfig?.dmsUrl?.trim().length > 0;
  }

  private isAutomaticPriceRequestEnabled(): boolean {
    return (this.isDcomConfigured() || !!this.dmsSettings.etk.isEnabled)
      && (this.outletRelatedConfig?.outletConfiguration?.retrievePricesAndAvailabilitiesAutomatically ?? false);
  }

  private calculateSettingsForBrowserMode(): void {
    this.dmsSettings.isAutomaticPriceRequestEnabled = false;
    if (this.isWritingToClipboardEnabled()) {
      // The transfer mode via clipboard supports some basic DMS functionalities
      this.dmsSettings.transferMode = TransferMode.Clipboard;
      this.dmsSettings.transferFormat =
        this.outletRelatedConfig?.outletConfiguration?.transferFormat || TransferFormat.Unknown;
    } else {
      this.dmsSettings.transferMode = TransferMode.Undefined;
      this.dmsSettings.transferFormat = TransferFormat.Unknown;
    }
  }

  private calculateSettingsForThinClientMode(): void {
    this.calculateSettingsForReadingFromEtkDms();
    this.calculateSettingsForReadingFromDcom();
    this.dmsSettings.isAutomaticPriceRequestEnabled = this.isAutomaticPriceRequestEnabled();
    if (ThinClientModeService.apasMode) {
      this.calculateSettingsForWritingToApas();
    } else {
      this.calculateSettingsForWritingToDms();
    }
  }

  private calculateSettingsForReadingFromDcom(): void {
    const dcom: DcomDmsSettings = this.dmsSettings.dcom;
    if (this.isDcomConfigured()) {
      const conf: OutletConfiguration = this.outletRelatedConfig.outletConfiguration;
      dcom.url = conf.dcomUrl;
      dcom.isPricesAndAvailabilitiesEnabled = true;
      dcom.isAutomaticPriceRequestEnabled = conf.retrievePricesAndAvailabilitiesAutomatically;
      dcom.requestPricesAndAvailabilitiesPerJob = conf.dmsOneRequestPerJobForPricesAndAvailabilities;
      dcom.isLicensePlateSearchEnabled = conf.airNumberPlateSearchAllowed;
      if (conf.brandSpecificWorkplaceIdUsed) {
        dcom.bmwWorkplaceId = conf.bmwWorkplaceId;
        dcom.bmwiWorkplaceId = conf.bmwiWorkplaceId;
        dcom.miniWorkplaceId = conf.miniWorkplaceId;
        dcom.rollsRoyceWorkplaceId = conf.rollsRoyceWorkplaceId;
        dcom.zinoroWorkplaceId = conf.zinoroWorkplaceId;
        dcom.bmwMotorradWorkplaceId = conf.bmwMotorcycleWorkplaceId;
      } else {
        dcom.workplaceId = conf.dcomWorkplaceId;
      }
      dcom.grpDmsOrderSplitActivated = conf.grpDmsOrderSplitActivated;
      dcom.grpDmsOrderSplitCostUnitAccountId = conf.grpDmsOrderSplitCostUnitAccountId;
      dcom.grpDmsOrderSplitCostUnitDescription = conf.grpDmsOrderSplitCostUnitDescription;
      dcom.sipDmsOrderSplitActivated = conf.sipDmsOrderSplitActivated;
      dcom.sipDmsOrderSplitCostUnitAccountId = conf.sipDmsOrderSplitCostUnitAccountId;
      dcom.sipDmsOrderSplitCostUnitDescription = conf.sipDmsOrderSplitCostUnitDescription;
      dcom.tecDmsOrderSplitActivated = conf.tecDmsOrderSplitActivated;
      dcom.tecDmsOrderSplitCostUnitAccountId = conf.tecDmsOrderSplitCostUnitAccountId;
      dcom.tecDmsOrderSplitCostUnitDescription = conf.tecDmsOrderSplitCostUnitDescription;
    } else {
      dcom.url = null;
      dcom.isPricesAndAvailabilitiesEnabled = false;
      dcom.isLicensePlateSearchEnabled = false;
    }
  }

  private calculateSettingsForWritingToDms(): void {
    if (this.isWritingToDmsEnabled()) { // DCOM/CLIPBOARD/FILE
      this.dmsSettings.transferMode =
        this.outletRelatedConfig.outletConfiguration.transferMode || TransferMode.Undefined;
      this.dmsSettings.transferFormat =
        this.outletRelatedConfig.outletConfiguration.transferFormat || TransferFormat.Unknown;
      this.dmsSettings.transferFileStorageLocation =
        this.outletRelatedConfig.outletConfiguration.dmsTransferFileStorageLocation;
      this.dmsSettings.lockJobsAfterTransfer = this.outletRelatedConfig.outletConfiguration.freezeJobsAfterDmsTransfer;
      this.dmsSettings.lockJobsAfterTransferAsPreOrder =
        this.outletRelatedConfig.outletConfiguration.freezeJobsAfterDmsTransferAsPreOrder;
      this.dmsSettings.dcom.overwriteMetadataOnUpdate =
        this.outletRelatedConfig.outletConfiguration.dcomOverwriteMetadataOnUpdate;
    } else {
      this.dmsSettings.transferMode = TransferMode.Undefined;
      this.dmsSettings.transferFormat = TransferFormat.Unknown;
    }
  }

  private calculateSettingsForReadingFromEtkDms(): void {
    // ETK XML Interface (read & write)
    this.dmsSettings.etk.isEnabled = this.isEtkDmsConfigured();
    this.dmsSettings.etk.isAutomaticPartsInfoEnabled = this.etkConfig ? this.etkConfig.activateDmsTeileInfo : false;
    this.dmsSettings.etk.isPartsInfoInPicturePanelEnabled = this.etkConfig
      ? this.etkConfig.activateDmsBestandsabfrage
      : false;
    this.dmsSettings.etk.useDcomForPricesAndAvailabilities = this.etkConfig?.useAwpDms;
    // ETK-XML Interface DMS transfer default values
    this.dmsSettings.etk.employeeNumber = this.etkConfig?.dmsMitarbeiternummer;
    this.dmsSettings.etk.password = this.etkConfig?.dmsPassword;
    this.dmsSettings.etk.orderNumber = this.etkConfig?.auftragsNr;
    this.dmsSettings.etk.customerNumber = this.etkConfig?.kundenNr;
    this.dmsSettings.etk.cashSalesCustomerNumber = this.etkConfig?.barverkaufKundennummer;
    // (ETK) US direct file interface
    this.dmsSettings.etk.isUsDirectEnabled = this.isWritingToUsDirectIsEnabled();
    this.dmsSettings.etk.usDirectFilename = this.isWritingToUsDirectIsEnabled() ?
      this.etkConfig.dmsDirectPath :
      undefined;
    // VAT rates
    this.dmsSettings.etk.vatRateCode1 = this.etkConfig?.mwstSatzCode1;
    this.dmsSettings.etk.vatRateCode2 = this.etkConfig?.mwstSatzCode2;
    this.dmsSettings.etk.vatRateCode3 = this.etkConfig?.mwstSatzCode3;
    this.dmsSettings.etk.vatRateCode4 = this.etkConfig?.mwstSatzCode4;
    this.dmsSettings.etk.vatRateCodeC = this.etkConfig?.mwstSatzCodeC;
    this.dmsSettings.etk.vatRateCodeL = this.etkConfig?.mwstSatzCodeL;
    this.dmsSettings.etk.vatRateReplacementParts = this.etkConfig?.atSteuersatz;
  }

  private calculateSettingsForWritingToApas(): void {
    this.dmsSettings.transferMode = TransferMode.Clipboard;
    this.dmsSettings.transferFormat = TransferFormat.Apas;
  }

  // TODO: Since BuNo is brand specific, DMS Lib needs to be re-initialized each time the active service case is changed
  private getDealerData(): DealerData {
    const user = this.getUser();
    // We are only checking for business number of selected vehicle type.
    const buNo: string = user.getBusinessNumber(undefined);
    console.log(Util.logPrefix('Webapp', 'Dms Service') + 'Using business number: ' + buNo);
    return new DealerData(
      buNo,
      this.getDpNo(user),
      this.getOutletNo(user),
      user.getLoginName(),
      DmsService.mapDefaultPartsSearchBrand(user.getDefaultPartsSearchBrand())
    );
  }

  private instantiateDmsApi(): void {
    const dmsApiTransferSettings: Settings = this.getDmsApiTransferSettings();
    const dealerData: DealerData = this.getDealerData();

    if (dmsApiTransferSettings.transferMode !== TransferMode.Undefined) {
      DmsService.loadDmsScripts();
    }

    this.dmsApi = DmsService.createDmsImpl(dmsApiTransferSettings, dealerData);
    this.subscribeToDmsCustomerDetermination();
    DmsService.createDmsStub(this.dmsApi, this.dmsIntercom);

    // Settings derived from the user (e.g. "Lagerort abfragen", default parts search brand) must be redistributed too
    const userDmsSettings: string = JSON.stringify([
      dmsApiTransferSettings.requestPartLocation,
      dealerData.defaultBrand
    ]);

    // only publish DMS settings if they have actually changed
    if (!this.previousOutletConfig || !this.previousEtkConfig
      || JSON.stringify(this.previousOutletConfig) !== JSON.stringify(this.outletRelatedConfig)
      || JSON.stringify(this.previousEtkConfig) !== JSON.stringify(this.etkConfig)
      || this.previousUserDmsSettings !== userDmsSettings) {
      this.previousOutletConfig = this.outletRelatedConfig;
      this.previousEtkConfig = this.etkConfig;
      this.previousUserDmsSettings = userDmsSettings;
      this.dmsSettingsChangedSubject.next(dmsApiTransferSettings);
    }
  }

  /**
   * Subscribes to customer IDs determined by the current DMS API instance. A new instance is created on every relevant
   * user/configuration change, so the subscription to the previous instance is removed first.
   */
  private subscribeToDmsCustomerDetermination(): void {
    this.unsubscribeFromDmsCustomerDetermination?.();
    this.unsubscribeFromDmsCustomerDetermination = this.dmsApi.subscribeToDetermineSingleCustomerId(
      (vin: string, customerId: string): void => this.dmsCustomerDeterminedSubject.next({vin, customerId})
    );
  }

  private getDmsApiTransferSettings(): Settings {
    const user = this.getUser();
    return new Settings(
      this.getTransferModeForDmsApi(),
      user.getLocale(),
      user.getB2XCountryCode(),
      this.dmsSettings.transferFileStorageLocation,
      this.dmsSettings.etk.usDirectFilename,
      this.dmsSettings.transferFormat,
      this.dmsSettings.dcom.url,
      this.getEtkTransferSettings(),
      user.getDmsUsername() || null,
      user.getDmsPassword() || null,
      this.getPricesAndAvailabilitiesRequestMethod(),
      ThinClientModeService.thinClientMode,
      undefined,
      undefined,
      this.dmsSettings.dcom.overwriteMetadataOnUpdate,
      this.getDcomUseUserTimezone(),
      user.getRequestPartLocation()
    );
  }

  private getTransferModeForDmsApi(): TransferMode {
    // Transfer mode needs to remain Dcom, if Dcom is configured to request prices or search via license plate
    return this.dmsSettings.transferMode === TransferMode.Undefined && this.isDcomConfigured() ?
      TransferMode.Dcom :
      this.dmsSettings.transferMode;
  }

  private getDcomUseUserTimezone(): DcomDateFormat {
    return this.outletRelatedConfig?.outletConfiguration.dcomUseUserTimezone
      ? DcomDateFormat.ISO8601_WITH_TIMEZONE_OFFSET
      : DcomDateFormat.ISO8601_IN_UTC;
  }

  private getPricesAndAvailabilitiesRequestMethod(): PricesAndAvailabilitiesRequestMethod {
    return this.dmsSettings.dcom.requestPricesAndAvailabilitiesPerJob
      ? PricesAndAvailabilitiesRequestMethod.ONE_REQUEST_PER_JOB
      : PricesAndAvailabilitiesRequestMethod.ONE_REQUEST_FOR_ALL_JOBS;
  }

  private getEtkTransferSettings(): EtkSettings {
    return this.etkConfig ?
      new EtkSettings(
        this.etkConfig.dmsUrl,
        this.etkConfig.dmsUsername,
        this.etkConfig.firmaNr,
        this.etkConfig.filialeNr
      ) :
      null;
  }

  private mergeOrderAndAppointmentResults(orders: OrderInfo[],
                                          appointments: AppointmentInfo[]): OrderAndAppointmentSearchResult[] {
    const result: OrderAndAppointmentSearchResult[] = [];
    orders.forEach((order: OrderInfo): void => {
      result.push({
        isDmsPreOrder: false,
        dmsId: order.serviceOrderNumber,
        customer: order.customer,
        vehicle: order.vehicle,
        lastChangedDateTime: order.lastChangedDateTime
      });
    });
    appointments.forEach((appointment: AppointmentInfo): void => {
      result.push({
        isDmsPreOrder: true,
        dmsId: appointment.serviceAppointmentId,
        customer: appointment.customer,
        vehicle: appointment.vehicle,
        lastChangedDateTime: appointment.lastChangedDateTime
      });
    });
    return result.sort((a: OrderAndAppointmentSearchResult, b: OrderAndAppointmentSearchResult): number => {
      if (a.lastChangedDateTime < b.lastChangedDateTime) {
        return 1;
      }
      if (a.lastChangedDateTime > b.lastChangedDateTime) {
        return -1;
      }
      return 0;
    });
  }

  /**
   * Make sure a language code matches the format xx-XX.
   * @param languageCode A language or country code, should be in the format xx, XX or xx-XX.
   * @returns The language code in the format xx-XX or undefined if the given code does not match the format above.
   */
  private normalizeLanguage(languageCode: string | undefined): string | undefined {
    // language can be xx-XX, xx, XX or undefined
    if (languageCode?.length === 2) {
      return languageCode.toLowerCase() + '-' + languageCode.toUpperCase();
    } else if (languageCode?.length === 5) {
      return languageCode;
    }
    return undefined;
  }

  private getUser(): UserData {
    return this.userService.getCurrentUser();
  }

  private getDpNo(user: UserData): string {
    return user.getBusinessPartner()?.getDistributionPartnerNumber();
  }

  private getOutletNo(user: UserData): string {
    return user.getBusinessPartner()?.getOutletNumber();
  }

  private isUsEnvironment(): boolean {
    return ['PROD_US', 'QA_US'].includes(environment.deploymentEnvironment);
  }

  private isProdUsEnvironment(): boolean {
    return environment.deploymentEnvironment === 'PROD_US';
  }

  // endregion
}
